import { ScanResult, ScanSummary, createScanSummary } from './types';
import { generateShareUrl, isDevShareMode } from '@/utils/shareEngine';

/**
 * Social Sharing QA Scan
 * Verifies all social sharing functionality, metadata, and branding
 */
export function runSocialShareScan(): ScanSummary {
  const results: ScanResult[] = [];
  
  console.group('🔗 Social Sharing QA Scan');
  
  // Check 1: Verify URL parameter format
  console.log('Checking URL parameter format...');
  const shareUrlFormat = checkShareUrlFormat();
  results.push(shareUrlFormat);
  
  // Check 2: Verify brand name consistency
  console.log('Checking brand name consistency...');
  const brandCheck = checkBrandName();
  results.push(brandCheck);
  
  // Check 3: Verify OG tags in DOM
  console.log('Checking OpenGraph tags...');
  const ogTagsCheck = checkOGTags();
  results.push(ogTagsCheck);
  
  // Check 4: Verify Twitter card tags
  console.log('Checking Twitter card tags...');
  const twitterCheck = checkTwitterTags();
  results.push(twitterCheck);
  
  // Check 5: Verify share image path
  console.log('Checking share image path...');
  const imageCheck = checkShareImage();
  results.push(imageCheck);
  
  // Check 6: Verify share text format
  console.log('Checking share text format...');
  const shareTextCheck = checkShareTextFormat();
  results.push(shareTextCheck);
  
  // Check 7: Verify Facebook share functionality
  console.log('Checking Facebook share...');
  const facebookCheck = checkFacebookShare();
  results.push(facebookCheck);
  
  // Check 8: Friend mode URLs removed
  results.push({
    name: 'Friend Mode URLs',
    status: 'info',
    message: 'Friend mode feature removed',
    details: 'Friend challenge URLs (?friend= param) removed from codebase'
  });
  
  // Check 9: Verify share URL domain
  console.log('Checking share URL domain...');
  const domainCheck = checkShareUrlDomain();
  results.push(domainCheck);
  
  const summary = createScanSummary('Social Sharing QA', results);
  
  console.log('\n📊 Social Sharing Scan Summary:');
  console.log(`✅ Passed: ${summary.passCount}`);
  console.log(`⚠️  Warnings: ${summary.warnCount}`);
  console.log(`❌ Failed: ${summary.failCount}`);
  console.table(results);
  console.groupEnd();
  
  return summary;
}

function checkShareUrlFormat(): ScanResult {
  try {
    // Check if shareEngine exports are using correct URL format
    const shareEngineContent = document.querySelector('[data-share-url]')?.textContent || '';
    const hasOldFormat = shareEngineContent.includes('?p=');
    const hasNewFormat = shareEngineContent.includes('?puzzle=');
    
    if (hasOldFormat && !hasNewFormat) {
      return {
        name: 'URL Parameter Format',
        status: 'fail',
        message: 'Still using old ?p= format',
        details: 'URLs should use ?puzzle= instead of ?p='
      };
    }
    
    return {
      name: 'URL Parameter Format',
      status: 'pass',
      message: 'Correct ?puzzle= URL format'
    };
  } catch (error) {
    return {
      name: 'URL Parameter Format',
      status: 'warn',
      message: 'Could not verify URL format',
      details: String(error)
    };
  }
}

function checkBrandName(): ScanResult {
  const bodyText = document.body.textContent || '';
  const hasTypo = bodyText.toLowerCase().includes('wormle');
  
  if (hasTypo) {
    return {
      name: 'Brand Name Consistency',
      status: 'fail',
      message: 'Found typo "Wormle" in page content',
      details: 'All instances should use "WordPop"'
    };
  }
  
  return {
    name: 'Brand Name Consistency',
    status: 'pass',
    message: 'Brand name "WordPop" used consistently'
  };
}

function checkOGTags(): ScanResult {
  const requiredTags = [
    'og:title',
    'og:description',
    'og:image',
    'og:url',
    'og:type',
    'og:site_name'
  ];
  
  const missingTags: string[] = [];
  const incorrectTags: string[] = [];
  
  requiredTags.forEach(tag => {
    const element = document.querySelector(`meta[property="${tag}"]`);
    if (!element) {
      missingTags.push(tag);
    } else {
      const content = element.getAttribute('content') || '';
      
      // Check for old image path
      if (tag === 'og:image' && !content.includes('/og/wordpop-share.png')) {
        incorrectTags.push(`${tag} uses old image path`);
      }
      
      // Check for correct domain
      if ((tag === 'og:url' || tag === 'og:image') && content.includes('wormble.app')) {
        incorrectTags.push(`${tag} uses wrong domain (wormble.app instead of wordpop.games)`);
      }
    }
  });
  
  if (missingTags.length > 0) {
    return {
      name: 'OpenGraph Tags',
      status: 'fail',
      message: `Missing OG tags: ${missingTags.join(', ')}`,
      details: 'All required OG tags must be present'
    };
  }
  
  if (incorrectTags.length > 0) {
    return {
      name: 'OpenGraph Tags',
      status: 'warn',
      message: 'Some OG tags need updating',
      details: incorrectTags.join(', ')
    };
  }
  
  return {
    name: 'OpenGraph Tags',
    status: 'pass',
    message: 'All OG tags present and correct'
  };
}

function checkTwitterTags(): ScanResult {
  const requiredTags = [
    'twitter:card',
    'twitter:title',
    'twitter:description',
    'twitter:image'
  ];
  
  const missingTags: string[] = [];
  const issues: string[] = [];
  
  requiredTags.forEach(tag => {
    const element = document.querySelector(`meta[name="${tag}"]`);
    if (!element) {
      missingTags.push(tag);
    } else {
      const content = element.getAttribute('content') || '';
      
      // Check card type
      if (tag === 'twitter:card' && content !== 'summary_large_image') {
        issues.push('twitter:card should be "summary_large_image"');
      }
      
      // Check image path
      if (tag === 'twitter:image' && !content.includes('/og/wordpop-share.png')) {
        issues.push('twitter:image uses old path');
      }
    }
  });
  
  if (missingTags.length > 0) {
    return {
      name: 'Twitter Card Tags',
      status: 'fail',
      message: `Missing tags: ${missingTags.join(', ')}`,
      details: 'All Twitter card tags must be present'
    };
  }
  
  if (issues.length > 0) {
    return {
      name: 'Twitter Card Tags',
      status: 'warn',
      message: 'Twitter tags need updating',
      details: issues.join(', ')
    };
  }
  
  return {
    name: 'Twitter Card Tags',
    status: 'pass',
    message: 'All Twitter tags present and correct'
  };
}

function checkShareImage(): ScanResult {
  const ogImage = document.querySelector('meta[property="og:image"]')?.getAttribute('content') || '';
  const twitterImage = document.querySelector('meta[name="twitter:image"]')?.getAttribute('content') || '';
  
  const correctPath = '/og/wordpop-share.png';
  const ogCorrect = ogImage.includes(correctPath);
  const twitterCorrect = twitterImage.includes(correctPath);
  
  if (!ogCorrect && !twitterCorrect) {
    return {
      name: 'Share Image Path',
      status: 'fail',
      message: 'Both OG and Twitter images use incorrect path',
      details: `Should use ${correctPath}`
    };
  }
  
  if (!ogCorrect || !twitterCorrect) {
    return {
      name: 'Share Image Path',
      status: 'warn',
      message: 'One share image path is incorrect',
      details: `OG: ${ogCorrect ? '✓' : '✗'}, Twitter: ${twitterCorrect ? '✓' : '✗'}`
    };
  }
  
  return {
    name: 'Share Image Path',
    status: 'pass',
    message: 'Share images use correct path'
  };
}

function checkShareTextFormat(): ScanResult {
  // This checks if the current page has share buttons we can inspect
  const shareButtons = document.querySelectorAll('[aria-label*="Share"], [aria-label*="Copy"]');
  
  if (shareButtons.length === 0) {
    return {
      name: 'Share Text Format',
      status: 'info',
      message: 'No share buttons on current page',
      details: 'Complete a game to test share text'
    };
  }
  
  return {
    name: 'Share Text Format',
    status: 'pass',
    message: 'Share buttons present',
    details: 'Text format should follow: "WordPop — I solved today\'s word in X guesses!"'
  };
}

function checkFacebookShare(): ScanResult {
  // Check for Facebook button in share modals OR check if share engine supports Facebook
  const facebookButton = document.querySelector('[aria-label*="Facebook"]');
  const facebookIcon = document.querySelector('.lucide-facebook');
  
  // If not on a results page, check if the share engine imports Facebook functionality
  const hasFacebookSupport = facebookButton || facebookIcon;
  
  if (!hasFacebookSupport) {
    // This is only a warning if we're not on a results page
    const isResultsPage = document.querySelector('[data-testid="result-modal"]') || 
                          document.querySelector('[role="dialog"]');
    return {
      name: 'Facebook Share',
      status: isResultsPage ? 'warn' : 'info',
      message: isResultsPage ? 'No Facebook share button found' : 'Facebook share available in post-game modal',
      details: 'Facebook sharing is implemented in ShareSheet component'
    };
  }
  
  return {
    name: 'Facebook Share',
    status: 'pass',
    message: 'Facebook share button present'
  };
}

function checkShareUrlDomain(): ScanResult {
  try {
    // Test the actual generateShareUrl function
    const testUrl = generateShareUrl(123);
    
    const isProduction = testUrl.startsWith('https://wordpop.games');
    const isDevMode = testUrl.startsWith(window.location.origin);
    const hasCorrectParams = testUrl.includes('?puzzle=123');
    
    // Check for wrong domains
    const wrongDomains = [
      'wormble.app',
      'wormble.com',
      'lovable.dev',
      'lovableproject.com',
      'localhost:8080',
      'supabase'
    ];
    
    const hasWrongDomain = wrongDomains.some(domain => testUrl.includes(domain));
    
    if (hasWrongDomain && !isDevMode) {
      return {
        name: 'Share URL Domain',
        status: 'fail',
        message: 'Share URLs pointing to wrong domain',
        details: `Generated URL: ${testUrl}\nExpected: https://wordpop.games/play?puzzle=...`
      };
    }
    
    if (!hasCorrectParams) {
      return {
        name: 'Share URL Domain',
        status: 'fail',
        message: 'Share URL missing required parameters',
        details: `Generated URL: ${testUrl}\nMissing ?puzzle= param`
      };
    }
    
    if (isDevMode && isDevShareMode()) {
      return {
        name: 'Share URL Domain',
        status: 'info',
        message: 'Dev mode enabled - using local URLs',
        details: `URL: ${testUrl}\nDisable dev mode for production URLs`
      };
    }
    
    return {
      name: 'Share URL Domain',
      status: 'pass',
      message: 'Share URLs correctly use wordpop.games',
      details: `Sample: ${testUrl}`
    };
  } catch (error) {
    return {
      name: 'Share URL Domain',
      status: 'fail',
      message: 'Failed to test share URL generation',
      details: String(error)
    };
  }
}
